import type {ReactNode} from 'react';
import {cx} from './cx';

/**
 * A card: white on Sticky Rice, one warm hairline, twelve-pixel corners, **flat**.
 *
 * There are no shadows on this surface. `--pokpok-shadow-low` exists in the
 * theme and is not for here — the design separates a card from the ground with a
 * hairline and nothing else, and a shadow added later to "lift" one card makes
 * every other card look sunken.
 *
 * # Tones are semantic, and one of them is the money rule
 *
 * * `default` — everything.
 * * `verified` — Pandan-tinted. **Server-verified money only.** Route the
 *   decision through `orderTotalTone`/`paymentTone` in `src/order/moneyTone.ts`;
 *   a screen must never choose this by hand. A card that merely looks positive —
 *   an order accepted, a spice level chosen, a payment submitted — is not this.
 * * `accent` — the "you will pay at the counter" notice. Tinted red and worded
 *   calmly, because nothing has gone wrong.
 * * `warning` — Palm Sugar. Something needs the guest's attention and is not an
 *   error: a sold-out line, an unpaid balance, an estimate that will change.
 */
export type CardTone = 'default' | 'verified' | 'accent' | 'warning';

export function Card({
  children,
  tone = 'default',
  /** `tight` for a dense list card, `flush` when the card holds its own rows. */
  padding = 'regular',
  className,
  as: Tag = 'div',
  role,
  'aria-live': ariaLive,
}: {
  children: ReactNode;
  tone?: CardTone;
  padding?: 'regular' | 'tight' | 'flush';
  className?: string;
  as?: 'div' | 'section' | 'li' | 'article';
  role?: string;
  'aria-live'?: 'polite' | 'assertive' | 'off';
}) {
  return (
    <Tag
      role={role}
      aria-live={ariaLive}
      className={cx(
        'pk-card',
        tone !== 'default' && `pk-card--${tone}`,
        padding === 'tight' && 'pk-card--tight',
        padding === 'flush' && 'pk-card--flush',
        className,
      )}
    >
      {children}
    </Tag>
  );
}

/**
 * A vertical stack with the standard gap. The internal layout of nearly every
 * card, and of most screen sections.
 */
export function Stack({
  children,
  gap = 'regular',
  className,
}: {
  children: ReactNode;
  gap?: 'regular' | 'wide';
  className?: string;
}) {
  return (
    <div className={cx('pk-stack', gap === 'wide' && 'pk-stack--wide', className)}>{children}</div>
  );
}

/** Label on the left, figure on the right. Every money row on the surface. */
export function SplitRow({children, className}: {children: ReactNode; className?: string}) {
  return <div className={cx('pk-split', className)}>{children}</div>;
}

/**
 * A section heading with an optional state pill on the right, and the hairline
 * under it: the spice/extras/kitchen-note headers on the item screen.
 *
 * `as` defaults to `h2` because these are real headings and a guest using a
 * screen reader navigates a long item page by them.
 */
export function SectionHeader({
  title,
  trailing,
  as: Tag = 'h2',
  className,
}: {
  title: ReactNode;
  /** A `StatusPill`, usually — "required", "optional", "sold out". */
  trailing?: ReactNode;
  as?: 'h2' | 'h3';
  className?: string;
}) {
  return (
    <div className={cx('pk-section-header', className)}>
      <Tag className="pk-section-title">{title}</Tag>
      {trailing}
    </div>
  );
}

/** The warm hairline, on its own. */
export function Divider({className}: {className?: string}) {
  return <hr className={cx('pk-divider', className)} />;
}
